import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Archive, ChevronRight, ChevronDown, ExternalLink, Pencil, Users as UsersIcon, FileText } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import BulkAssignTaskModal from "@/components/BulkAssignTaskModal";

/**
 * AdminTasks — scalable task overview.
 *
 * • Group-by control: User (assignee), Team (assignee's team_section), or All.
 * • Every group header is collapsed by default so the page stays fast even
 *   when thousands of tasks exist across dozens of teams.
 * • Task rows are lazy-loaded per group on expand, hitting `/admin/tasks`
 *   with the appropriate filter. Aggregate counts come from
 *   `/admin/tasks/groups` (single indexed aggregation, no row transfer).
 */
const COLUMNS = [
  { key: "todo", label: "To do" },
  { key: "in_progress", label: "In progress" },
  { key: "review", label: "Review" },
  { key: "done", label: "Done" },
];
const GROUP_BY_OPTIONS = [
  { key: "task", label: "Task (assignment)" },
  { key: "user", label: "User" },
  { key: "team", label: "Team" },
  { key: "none", label: "All (flat)" },
];

export default function AdminTasks() {
  const [groupBy, setGroupBy] = useState("task");
  const [showArchived, setShowArchived] = useState(false);
  const [groups, setGroups] = useState([]);
  const [flatTasks, setFlatTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({}); // { [group.key]: { loaded, tasks[] } }
  const [showNew, setShowNew] = useState(false);
  const [users, setUsers] = useState([]);
  const [rowOpen, setRowOpen] = useState({});
  const [editTask, setEditTask] = useState(null); // task obj or null
  const [previewGroup, setPreviewGroup] = useState(null); // {title, description, assignees[]} or null

  // Prefetch the user list once (for filter descriptions + team lookup used by
  // group-by=team). Not required for group headers themselves — those come
  // from the aggregation endpoint.
  useEffect(() => {
    api.get("/admin/users?role=&q=&page=1&page_size=500")
      .then((r) => setUsers((r.data?.items || r.data || []).filter((u) => u.role !== "client")))
      .catch(() => {});
  }, []);

  const usersById = React.useMemo(() => {
    const m = new Map();
    for (const u of users) m.set(u.user_id, u);
    return m;
  }, [users]);

  const loadGroups = async () => {
    setLoading(true);
    setExpanded({});
    try {
      if (groupBy === "none") {
        const r = await api.get(`/admin/tasks?${showArchived ? "include_archived=true" : ""}`);
        setFlatTasks(r.data || []);
        setGroups([]);
      } else if (groupBy === "task") {
        // Client-side aggregate by bulk_id (falls back to task id for legacy
        // single-assignee tasks). Each group represents one "assignment event".
        const r = await api.get(`/admin/tasks?${showArchived ? "include_archived=true" : ""}`);
        const rows = r.data || [];
        const map = new Map();
        for (const t of rows) {
          const key = t.bulk_id || `solo_${t.id}`;
          const g = map.get(key) || {
            key, label: t.title, tasks: [], sample: t,
            todo: 0, in_progress: 0, review: 0, done: 0, count: 0,
            submitted: 0, progress_updates: 0, comments: 0,
          };
          g.tasks.push(t);
          g[t.status] = (g[t.status] || 0) + 1;
          g.count += 1;
          if ((t.deliverable_count || 0) > 0 || t.submission_url) g.submitted += 1;
          if ((t.progress_updates_count || 0) > 0) g.progress_updates += 1;
          if ((t.comments_count || 0) > 0) g.comments += 1;
          if (!g.label) g.label = t.title;
          map.set(key, g);
        }
        const arr = Array.from(map.values()).sort((a, b) =>
          (b.sample?.created_at || "").localeCompare(a.sample?.created_at || ""));
        setGroups(arr);
        setFlatTasks([]);
      } else {
        const r = await api.get(`/admin/tasks/groups?group_by=${groupBy}&include_archived=${showArchived}`);
        setGroups(r.data || []);
        setFlatTasks([]);
      }
    } catch { toast.error("Couldn't load tasks"); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadGroups(); /* eslint-disable-next-line */ }, [groupBy, showArchived]);

  const toggleGroup = async (g) => {
    const cur = expanded[g.key];
    if (cur?.loaded) {
      setExpanded((e) => ({ ...e, [g.key]: null }));
      return;
    }
    // For "task" group-by we already have all tasks inline — no fetch needed.
    if (groupBy === "task") {
      setExpanded((e) => ({ ...e, [g.key]: { loaded: true, tasks: g.tasks || [] } }));
      return;
    }
    // Optimistically mark as loading
    setExpanded((e) => ({ ...e, [g.key]: { loaded: false, tasks: [] } }));
    try {
      let url = `/admin/tasks?${showArchived ? "include_archived=true" : ""}`;
      let rows = [];
      if (groupBy === "user") {
        url += `&assignee=${encodeURIComponent(g.key)}`;
        const r = await api.get(url);
        rows = r.data || [];
      } else if (groupBy === "team") {
        // No team-scoped endpoint yet → filter client-side using the users cache.
        const r = await api.get(url);
        const teamMembers = new Set(users.filter((u) => (u.team_section || "Unassigned") === g.key).map((u) => u.user_id));
        rows = (r.data || []).filter((t) => teamMembers.has(t.assignee_user_id));
      }
      setExpanded((e) => ({ ...e, [g.key]: { loaded: true, tasks: rows } }));
    } catch {
      toast.error("Couldn't expand group");
      setExpanded((e) => ({ ...e, [g.key]: null }));
    }
  };

  const patchTaskInExpanded = (gKey, taskId, patch) => {
    setExpanded((e) => {
      const g = e[gKey]; if (!g) return e;
      return { ...e, [gKey]: { ...g, tasks: g.tasks.map((t) => t.id === taskId ? { ...t, ...patch } : t) } };
    });
  };
  const removeTaskFromExpanded = (gKey, taskId) => {
    setExpanded((e) => {
      const g = e[gKey]; if (!g) return e;
      return { ...e, [gKey]: { ...g, tasks: g.tasks.filter((t) => t.id !== taskId) } };
    });
  };

  const updateTask = async (t, patch, gKey) => {
    try {
      await api.put(`/employee/tasks/${t.id}`, patch);
      if (gKey) patchTaskInExpanded(gKey, t.id, patch);
      else setFlatTasks((arr) => arr.map((x) => x.id === t.id ? { ...x, ...patch } : x));
    } catch { toast.error("Update failed"); }
  };
  const removeTask = async (t, gKey) => {
    if (!window.confirm(`Delete task "${t.title}"?`)) return;
    try {
      await api.delete(`/employee/tasks/${t.id}`);
      if (gKey) { removeTaskFromExpanded(gKey, t.id); loadGroups(); }
      else setFlatTasks((arr) => arr.filter((x) => x.id !== t.id));
    } catch { toast.error("Delete failed"); }
  };
  const openWorkspace = async (t) => {
    // Open in a new tab so the assigner keeps the task board in view. We
    // provision the workspace first, then window.open() with the returned id.
    try {
      const r = await api.post(`/employee/tasks/${t.id}/workspace`);
      const gid = r.data?.guidance_id;
      if (gid) window.open(`/dashboard/tasks/${gid}`, "_blank", "noopener,noreferrer");
    } catch { toast.error("Couldn't open workspace"); }
  };

  const exportSummaryPdf = async (g) => {
    // Group summary → send bulk_id when we have it, otherwise fall back to
    // the single task id. Backend uploads to the task's Drive title-folder
    // and also returns the raw PDF so we can trigger a browser download.
    const bulkId = g.sample?.bulk_id;
    const taskId = g.sample?.id;
    const params = bulkId ? `bulk_id=${encodeURIComponent(bulkId)}` : `task_id=${encodeURIComponent(taskId)}`;
    const t = toast.loading("Generating task summary…");
    try {
      const r = await api.post(`/admin/tasks/summary-pdf?${params}`);
      // Trigger local download.
      const bin = atob(r.data.pdf_base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = r.data.filename || "task-summary.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.dismiss(t);
      if (r.data.drive_file?.webViewLink) {
        toast.success("Summary generated & uploaded to Drive", {
          action: { label: "Open in Drive", onClick: () => window.open(r.data.drive_file.webViewLink, "_blank") },
        });
      } else {
        toast.success(`Summary generated (${r.data.assignees} assignees) — Drive upload skipped`);
      }
    } catch (e) {
      toast.dismiss(t);
      toast.error(e?.response?.data?.detail || "Couldn't export summary");
    }
  };


  // ---------- Renderers ----------
  const STATUS_STYLES = {
    todo: "bg-slate-100 text-kb-ink-muted border-slate-200",
    in_progress: "bg-amber-50 text-amber-700 border-amber-200",
    review: "bg-sky-50 text-sky-700 border-sky-200",
    done: "bg-emerald-50 text-emerald-700 border-emerald-200",
  };
  const statusLabel = (s) => (COLUMNS.find((c) => c.key === s)?.label) || s;

  const renderRow = (t, gKey) => {
    const isOpen = !!rowOpen[t.id];
    const inTaskGroup = groupBy === "task";
    // In the "By Task" view every row already sits under the task header,
    // so leading with the assignee name (not the repeated task title) is
    // far easier to scan.
    const primary = inTaskGroup
      ? (t.assignee_name || t.assignee_user_id || "Unassigned")
      : t.title;
    return (
      <div key={t.id} className="border-b border-slate-100 last:border-0" data-testid={`admin-task-${t.id}`}>
        <div className="px-4 py-2.5 flex flex-wrap items-center gap-2.5">
          <button
            type="button"
            onClick={() => setRowOpen((prev) => ({ ...prev, [t.id]: !prev[t.id] }))}
            className="p-0.5 text-kb-ink-muted hover:text-kb-ink"
            data-testid={`admin-task-${t.id}-expand`}
            title={isOpen ? "Hide details" : "Show details"}
          >
            {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>
          <div className="flex-1 min-w-0 flex items-center gap-2">
            {inTaskGroup && (
              <div className="w-7 h-7 rounded-full bg-kb-primary/10 text-kb-primary flex items-center justify-center text-[11px] font-medium shrink-0" title={t.assignee_name} data-testid={`admin-task-${t.id}-avatar`}>
                {(t.assignee_name || "?")[0]?.toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-sm text-kb-ink font-medium truncate">
                {primary}
                {t.archived && <span className="ml-2 kb-mono text-[9px] text-kb-ink-light">ARCHIVED</span>}
              </div>
              <div className="kb-mono text-[10px] text-kb-ink-muted truncate">
                {inTaskGroup ? statusLabel(t.status) : `${t.priority} · assigned by ${t.assigner_name || "system"}${t.due_date ? ` · due ${t.due_date}` : ""}`}
                {typeof t.progress_pct === "number" ? ` · ${t.progress_pct}% done` : ""}
              </div>
              <TaskActivityChips t={t} />
            </div>
          </div>
          {inTaskGroup ? (
            <span className={`kb-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${STATUS_STYLES[t.status] || STATUS_STYLES.todo}`} data-testid={`admin-task-${t.id}-status-badge`}>
              {statusLabel(t.status)}
            </span>
          ) : (
            <select value={t.status} onChange={(e) => updateTask(t, { status: e.target.value }, gKey)} className="kb-input text-[11px] py-1 px-2">
              {COLUMNS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          )}
          <button onClick={() => openWorkspace(t)} className="kb-btn-outline text-[11px] inline-flex items-center gap-1" data-testid={`admin-task-${t.id}-workspace`}>
            Workspace <ExternalLink className="w-3 h-3" />
          </button>
          <button onClick={() => setEditTask(t)} className="text-kb-ink-muted hover:text-kb-primary p-1" title="Edit task" data-testid={`admin-task-${t.id}-edit`}>
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => updateTask(t, { archived: !t.archived }, gKey)} className="text-kb-ink-muted hover:text-kb-ink p-1" title={t.archived ? "Restore" : "Archive"}>
            <Archive className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => removeTask(t, gKey)} className="text-rose-500 hover:text-rose-700 p-1">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        {isOpen && (
          <div className="px-6 pb-3 bg-slate-50 border-t border-slate-100">
            {t.description ? (
              <div className="kb-task-md text-sm text-kb-ink leading-relaxed pt-2" data-testid={`admin-task-${t.id}-description`}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{t.description}</ReactMarkdown>
              </div>
            ) : (
              <div className="text-xs text-kb-ink-muted italic pt-2">No description provided.</div>
            )}
            {t.submission_url && (
              <div className="mt-2 kb-mono text-[10px] uppercase tracking-wider">
                Submission: <a href={t.submission_url} target="_blank" rel="noreferrer" className="text-kb-primary hover:underline">{t.submission_url}</a>
              </div>
            )}
            <TaskCommentThread task={t} />
          </div>
        )}
      </div>
    );
  };

  const renderGroup = (g) => {
    const open = !!expanded[g.key];
    const state = expanded[g.key];
    const sub = usersById.get(g.key);
    const isTaskGroup = groupBy === "task";
    const sample = g.sample || (g.tasks && g.tasks[0]) || {};
    const total = g.count || 0;
    const donePct = total ? Math.round(((g.done || 0) / total) * 100) : 0;
    return (
      <div key={g.key} className="border border-slate-200 rounded-sm bg-white overflow-hidden" data-testid={`admin-tasks-group-${g.key}`}>
        <div className={`w-full px-4 py-3 border-b border-slate-200 hover:bg-slate-50 transition-colors ${isTaskGroup ? "bg-white" : "bg-slate-50"}`}>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => toggleGroup(g)}
              className="flex items-center gap-3 flex-1 min-w-0 text-left"
              data-testid={`admin-tasks-group-${g.key}-toggle`}
            >
              {open ? <ChevronDown className="w-4 h-4 text-kb-ink-muted shrink-0" /> : <ChevronRight className="w-4 h-4 text-kb-ink-muted shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-kb-ink truncate">{g.label || g.key}</div>
                {groupBy === "user" && sub && (
                  <div className="kb-mono text-[10px] text-kb-ink-light truncate">
                    {sub.role}{sub.employee_role ? ` · ${sub.employee_role}` : ""}{sub.team_section ? ` · ${sub.team_section}` : ""}
                  </div>
                )}
                {isTaskGroup && (
                  <div className="kb-mono text-[10px] text-kb-ink-light truncate">
                    <span className="uppercase tracking-wider">{sample.priority || "medium"}</span>
                    {" · "}by {sample.assigner_name || "system"}
                    {sample.due_date ? ` · due ${sample.due_date}` : ""}
                    {" · "}{g.count} assignee{g.count === 1 ? "" : "s"}
                  </div>
                )}
              </div>
            </button>
            <div className="flex items-center gap-1.5 shrink-0">
              {isTaskGroup ? (
                <>
                  {[
                    { k: "todo", cls: "bg-slate-100 text-kb-ink-muted", label: "To do" },
                    { k: "in_progress", cls: "bg-amber-50 text-amber-700 border border-amber-200", label: "In progress" },
                    { k: "review", cls: "bg-sky-50 text-sky-700 border border-sky-200", label: "Review" },
                    { k: "done", cls: "bg-emerald-50 text-emerald-700 border border-emerald-200", label: "Done" },
                  ].filter((s) => (g[s.k] || 0) > 0).map((s) => (
                    <span key={s.k} title={s.label} className={`kb-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm font-medium ${s.cls}`}>
                      {g[s.k]} {s.label}
                    </span>
                  ))}
                  {(g.submitted || 0) > 0 && (
                    <span title="Assignees with at least one deliverable submitted" className="kb-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
                      {g.submitted} Submitted
                    </span>
                  )}
                  {(g.progress_updates || 0) > 0 && (
                    <span title="Assignees with at least one progress log entry" className="kb-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm font-medium bg-slate-100 text-kb-ink-muted border border-slate-200">
                      {g.progress_updates} Progress
                    </span>
                  )}
                  {(g.comments || 0) > 0 && (
                    <span title="Assignees with at least one task comment" className="kb-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm font-medium bg-slate-100 text-kb-ink-muted border border-slate-200">
                      {g.comments} Comments
                    </span>
                  )}
                </>
              ) : (
                <>
                  <span title="To do" className="kb-mono text-[10px] px-1.5 py-0.5 rounded-sm bg-slate-100 text-kb-ink-muted">{g.todo || 0}</span>
                  <span title="In progress" className="kb-mono text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-50 text-amber-700 border border-amber-100">{g.in_progress || 0}</span>
                  <span title="Review" className="kb-mono text-[10px] px-1.5 py-0.5 rounded-sm bg-sky-50 text-sky-700 border border-sky-100">{g.review || 0}</span>
                  <span title="Done" className="kb-mono text-[10px] px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-100">{g.done || 0}</span>
                </>
              )}
              {isTaskGroup && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); setPreviewGroup({ ...g, sample }); }}
                    className="text-kb-ink-muted hover:text-kb-primary p-1 ml-1"
                    title="Preview description"
                    data-testid={`admin-tasks-group-${g.key}-preview`}
                  >
                    <UsersIcon className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); exportSummaryPdf(g); }}
                    className="text-kb-ink-muted hover:text-kb-primary p-1"
                    title="Export summary PDF (also uploads to Drive)"
                    data-testid={`admin-tasks-group-${g.key}-summary`}
                  >
                    <FileText className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setEditTask({ ...sample, __bulk_group: g }); }}
                    className="text-kb-ink-muted hover:text-kb-primary p-1"
                    title="Edit for all assignees"
                    data-testid={`admin-tasks-group-${g.key}-edit`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
          </div>
          {isTaskGroup && total > 0 && (
            <div className="mt-2.5 flex items-center gap-3">
              <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden" data-testid={`admin-tasks-group-${g.key}-progress`}>
                <div className="h-full bg-emerald-500 transition-all" style={{ width: `${donePct}%` }} />
              </div>
              <span className="kb-mono text-[10px] text-kb-ink-muted tabular-nums shrink-0">{donePct}% done</span>
            </div>
          )}
        </div>
        {open && (
          <div>
            {!state?.loaded ? (
              <div className="px-4 py-3 kb-mono text-[10px] text-kb-ink-muted">Loading…</div>
            ) : state.tasks.length === 0 ? (
              <div className="px-4 py-3 text-xs text-kb-ink-muted">No tasks in this group.</div>
            ) : state.tasks.map((t) => renderRow(t, g.key))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div data-testid="admin-tasks-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">ADMIN · TASKS</div>
          <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Employee <em className="italic text-kb-primary">tasks</em></h1>
          <p className="text-sm text-kb-ink-muted mt-2 max-w-2xl">Every task assigned to any employee or intern. Group by teammate or team, expand only what you need — the page stays fast even at scale.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowArchived((v) => !v)}
            className={`text-xs kb-mono uppercase tracking-wider px-3 py-1.5 border rounded-sm ${showArchived ? "bg-kb-ink text-white border-kb-ink" : "border-slate-300 text-kb-ink-muted hover:border-kb-ink"}`}
            data-testid="admin-tasks-toggle-archive"
          >
            <Archive className="w-3.5 h-3.5 inline mr-1" /> {showArchived ? "Hide archived" : "Show archived"}
          </button>
          <button onClick={() => setShowNew(true)} className="kb-btn-primary text-sm" data-testid="admin-tasks-new-btn">
            <Plus className="w-4 h-4" /> Assign task
          </button>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Group by</label>
        <div className="inline-flex rounded-sm border border-slate-200 overflow-hidden">
          {GROUP_BY_OPTIONS.map((o) => (
            <button
              key={o.key}
              type="button"
              onClick={() => setGroupBy(o.key)}
              className={`px-3 py-1.5 kb-mono text-[10px] uppercase tracking-wider border-r border-slate-200 last:border-r-0 transition-colors ${groupBy === o.key ? "bg-kb-ink text-white" : "text-kb-ink-muted hover:text-kb-ink hover:bg-slate-50"}`}
              data-testid={`admin-tasks-group-by-${o.key}`}
            >
              {o.label}
            </button>
          ))}
        </div>
        {groupBy !== "none" && groups.length > 0 && (
          <button
            type="button"
            onClick={() => {
              const allOpen = groups.every((g) => expanded[g.key]?.loaded);
              if (allOpen) setExpanded({});
              else groups.forEach((g) => { if (!expanded[g.key]?.loaded) toggleGroup(g); });
            }}
            className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary hover:underline"
            data-testid="admin-tasks-expand-all"
          >
            {groups.every((g) => expanded[g.key]?.loaded) ? "Collapse all" : "Expand all"}
          </button>
        )}
      </div>

      {loading ? (
        <div className="mt-8 kb-mono text-xs text-kb-ink-muted">Loading…</div>
      ) : groupBy === "none" ? (
        flatTasks.length === 0 ? (
          <div className="mt-10 text-center py-16 border border-dashed border-slate-300 rounded-sm bg-white text-sm text-kb-ink-muted">No tasks yet.</div>
        ) : (
          <div className="mt-6 border border-slate-200 rounded-sm bg-white overflow-hidden">
            {flatTasks.map((t) => renderRow(t, null))}
          </div>
        )
      ) : groups.length === 0 ? (
        <div className="mt-10 text-center py-16 border border-dashed border-slate-300 rounded-sm bg-white text-sm text-kb-ink-muted">No tasks match this filter.</div>
      ) : (
        <div className="mt-6 space-y-3">{groups.map(renderGroup)}</div>
      )}

      {showNew && (
        <BulkAssignTaskModal
          open={showNew}
          onClose={() => setShowNew(false)}
          onCreated={loadGroups}
          restrictToInterns={false}
        />
      )}

      {editTask && (
        <TaskEditModal
          task={editTask}
          onClose={() => setEditTask(null)}
          onSaved={() => { setEditTask(null); loadGroups(); }}
        />
      )}

      {previewGroup && (
        <TaskGroupPreviewModal
          group={previewGroup}
          onClose={() => setPreviewGroup(null)}
        />
      )}
    </div>
  );
}

/** Compact status pills — shown under every task row so the assigner can
 *  tell at a glance whether the assignee has moved beyond the Kanban board:
 *  submitted deliverable, logged progress, or posted comments. Emphasises
 *  the *latest* deliverable status when relevant. */
function TaskCommentThread({ task }) {
  const [comments, setComments] = React.useState(null);
  const [text, setText] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    let live = true;
    api.get(`/employee/tasks/${task.id}/comments`)
      .then((r) => { if (live) setComments(r.data || []); })
      .catch(() => { if (live) setComments([]); });
    return () => { live = false; };
  }, [task.id]);
  const send = async (e) => {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    setBusy(true);
    try {
      const r = await api.post(`/employee/tasks/${task.id}/comments`, { body });
      setComments((prev) => [...(prev || []), r.data]);
      setText("");
    } catch { toast.error("Reply failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 border-t border-slate-200 pt-2.5" data-testid={`admin-task-${task.id}-comments`}>
      <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Comments · Q&amp;A</div>
      <div className="mt-1.5 space-y-1.5 max-h-56 overflow-y-auto">
        {comments === null && <div className="text-[10px] text-kb-ink-light italic">Loading…</div>}
        {comments && comments.length === 0 && <div className="text-[10px] text-kb-ink-light italic">No comments yet.</div>}
        {(comments || []).map((c) => (
          <div key={c.id} className="border border-slate-200 rounded-sm p-2 bg-white">
            <div className="kb-mono text-[9px] uppercase tracking-wider text-kb-ink-light flex items-center gap-1.5">
              <span className="text-kb-ink">{c.author_name || "Someone"}</span>
              {["admin", "mentor", "tutor", "employee"].includes(c.author_role) && (
                <span className="text-[8px] px-1 py-0.5 rounded-sm bg-kb-primary/10 text-kb-primary">ASSIGNER</span>
              )}
              <span>{new Date(c.created_at).toLocaleString()}</span>
            </div>
            <div className="text-[12px] text-kb-ink mt-0.5 whitespace-pre-wrap">{c.body}</div>
          </div>
        ))}
      </div>
      <form onSubmit={send} className="mt-2 flex gap-1.5">
        <input type="text" value={text} onChange={(e) => setText(e.target.value)}
          placeholder="Reply to the assignee…"
          className="kb-input text-[11px] flex-1"
          data-testid={`admin-task-${task.id}-comment-input`} />
        <button type="submit" disabled={busy || !text.trim()} className="kb-btn-primary text-[10px] px-3"
          data-testid={`admin-task-${task.id}-comment-send`}>
          {busy ? "…" : "Reply"}
        </button>
      </form>
    </div>
  );
}

function TaskActivityChips({ t }) {
  const chips = [];
  const submitted = !!t.submission_url;
  if (submitted) chips.push({ k: "sub", label: "Submitted", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" });
  const dc = t.deliverable_count || 0;
  if (dc > 0) {
    const s = t.latest_deliverable_status;
    const cls = s === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
             : s === "revision_requested" ? "bg-amber-50 text-amber-700 border-amber-200"
             : "bg-sky-50 text-sky-700 border-sky-200";
    const label = s ? s.replace(/_/g, " ") : "deliverable";
    chips.push({ k: "d", label: `${dc} deliverable${dc === 1 ? "" : "s"} · ${label}`, cls });
  }
  if ((t.progress_updates_count || 0) > 0) {
    chips.push({ k: "p", label: `${t.progress_updates_count} progress update${t.progress_updates_count === 1 ? "" : "s"}`, cls: "bg-slate-100 text-kb-ink-muted border-slate-200" });
  }
  if ((t.comments_count || 0) > 0) {
    chips.push({ k: "c", label: `${t.comments_count} comment${t.comments_count === 1 ? "" : "s"}`, cls: "bg-slate-100 text-kb-ink-muted border-slate-200" });
  }
  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-0.5">
      {chips.map((c) => (
        <span key={c.k} className={`kb-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${c.cls}`}>{c.label}</span>
      ))}
    </div>
  );
}

/** Edit a single task, or every task in a bulk-assignment group. Applies
 *  title/description/priority/due_date via PUT /employee/tasks/{id}. */
function TaskEditModal({ task, onClose, onSaved }) {
  const isBulk = !!task.__bulk_group;
  const group = task.__bulk_group;
  const [form, setForm] = React.useState({
    title: task.title || "",
    description: task.description || "",
    priority: task.priority || "medium",
    due_date: task.due_date || "",
  });
  const [busy, setBusy] = React.useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const payload = { title: form.title, description: form.description, priority: form.priority, due_date: form.due_date || null };
      if (isBulk) {
        await Promise.all((group.tasks || []).map((t) => api.put(`/employee/tasks/${t.id}`, payload)));
        toast.success(`Updated ${group.tasks.length} task${group.tasks.length === 1 ? "" : "s"}`);
      } else {
        await api.put(`/employee/tasks/${task.id}`, payload);
        toast.success("Task updated");
      }
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="task-edit-modal">
      <div className="bg-white rounded-sm shadow-2xl max-w-lg w-full p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-xl text-kb-ink">Edit {isBulk ? `${group.count} assignments` : "task"}</h3>
          <button onClick={onClose} className="text-kb-ink-muted hover:text-kb-primary" data-testid="task-edit-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Title</label>
            <input className="kb-input mt-1" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-edit-title" />
          </div>
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Description (markdown)</label>
            <textarea rows={6} className="kb-input mt-1 font-mono text-sm" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="task-edit-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Priority</label>
              <select className="kb-input mt-1" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="task-edit-priority">
                <option value="low">low</option><option value="medium">medium</option><option value="high">high</option>
              </select>
            </div>
            <div>
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Due date</label>
              <input type="date" className="kb-input mt-1" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="task-edit-due" />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="kb-btn-secondary text-sm">Cancel</button>
          <button onClick={save} disabled={busy} className="kb-btn-primary text-sm" data-testid="task-edit-save">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Read-only preview of the task description + list of assignees. Used from
 *  the "By Task" grouping so an assigner can quickly see what they assigned
 *  and to whom without expanding the whole row list. */
function TaskGroupPreviewModal({ group, onClose }) {
  const sample = group.sample || {};
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="task-group-preview">
      <div className="bg-white rounded-sm shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-slate-200 sticky top-0 bg-white">
          <div className="min-w-0">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">TASK</div>
            <h3 className="font-serif text-xl text-kb-ink mt-0.5 truncate">{group.label}</h3>
            <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-1">
              {sample.priority}{sample.due_date ? ` · Due ${sample.due_date}` : ""}
              {sample.assigner_name ? ` · by ${sample.assigner_name}` : ""}
            </div>
          </div>
          <button onClick={onClose} className="text-kb-ink-muted hover:text-kb-primary" data-testid="task-group-preview-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">DESCRIPTION</div>
          <div className="kb-task-md mt-2 text-sm leading-relaxed">
            {sample.description
              ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{sample.description}</ReactMarkdown>
              : <span className="italic text-kb-ink-muted">No description provided.</span>}
          </div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mt-5">ASSIGNEES · {group.count}</div>
          <ul className="mt-2 space-y-1">
            {(group.tasks || []).map((t) => (
              <li key={t.id} className="text-sm text-kb-ink flex items-center justify-between gap-2 border-b border-slate-100 py-1">
                <span className="truncate">{t.assignee_name || t.assignee_user_id}</span>
                <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">{t.status}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
